/**
 * Chain parameters for the FACE network.
 *
 * Everything here is consensus-critical: two nodes that disagree on any of
 * these values will fork. Testnets override via `loadParams()`.
 */

export interface ChainParams {
  /** Human-readable chain id, mixed into every signature preimage. */
  chainId: string;
  /** Unix ms at which halving epoch 0 begins. */
  genesisTimeMs: number;
  /** Length of one halving epoch in ms. */
  halvingPeriodMs: number;
  /** Genesis allocation for the first face, in grains. */
  genesisMintGrains: bigint;
  /** Invites that fully unlock an airdrop; each one releases 1/n of it. */
  unlockInvites: number;
  /**
   * Claims allowed before a voucher is required. The bootstrap window: it
   * has to be open for the first people, because there is nobody to vouch
   * for them yet.
   */
  openClaims: number;
  /** New humans one registered identity may vouch for, per epoch. */
  vouchesPerEpoch: number;
  /**
   * Transfers at or above this size do not land immediately.
   *
   * Coercion cannot be prevented - a person who can open a wallet can be
   * made to open it. What can be changed is what the coercer walks away
   * with. Above this threshold the money enters escrow, and the sender can
   * cancel it for the length of the guard window. That turns "hand it over
   * and we leave" into "keep control of a person for a day, and stay
   * reachable", which is a materially worse crime to commit.
   *
   * Small payments are unaffected, so the shop still works.
   */
  guardThresholdGrains: bigint;
  /** How long a guarded transfer can be cancelled for. */
  guardWindowMs: number;
  /**
   * How long a device change waits before it takes effect.
   *
   * A wallet bound to a lost phone would otherwise be frozen forever, so
   * the key its owner still derives from their face must be able to name a
   * new authenticator. That same key is derivable from a photograph on
   * platforms without PRF, so the change cannot be immediate: it is
   * announced, and the *current* device can veto it for this long. Someone
   * who still holds their phone always wins; someone who genuinely lost it
   * is not locked out for good.
   */
  deviceChangeWindowMs: number;
  /** Public domain-separation pepper for the biometric LSH projections. */
  lshPepper: string;
  /** Max clock skew tolerated on an incoming vertex, ms. */
  maxClockSkewMs: number;
  /**
   * How far below its own parents' timestamps a vertex may claim to be.
   *
   * Much tighter than maxClockSkewMs, which exists for clocks that run
   * fast. Backdating is the direction an attacker wants: it shortens an
   * escrow window. A vertex builds on parents it has already seen, so its
   * honest time is at or after theirs, give or take a slow clock.
   */
  maxBackdateMs: number;
  /** Vertices may not reference fewer / more parents than this. */
  minParents: number;
  maxParents: number;
  /** Baseline anti-spam proof-of-work difficulty, in leading zero bits. */
  basePowBits: number;
  /** Extra PoW bits charged per tx already issued by a sender in the window. */
  powRateWindowMs: number;
  powRateFreeTx: number;
  /** Hard ceiling so a single vertex can never be unaffordable on a phone. */
  maxPowBits: number;
}

export const GRAINS_PER_FACE = 100_000_000n; // 8 decimals
export const FACE_DECIMALS = 8;

export const MAINNET: ChainParams = {
  // face-main-1 -> -2 was the weekly halving, the 65 536 base and locked
  // airdrops: consensus-visible changes, so a new id rather than letting
  // old and new nodes silently disagree.
  //
  // -6 -> -7 removes the hand check. It did not work: the landmarker it
  // depended on could not be made to detect a hand reliably on real
  // devices, and a claim requirement that cannot be satisfied is a wallet
  // nobody can open. A mint no longer carries hand evidence and no longer
  // publishes finger material, so -6 and -7 disagree about every claim.
  //
  // -5 -> -6 reworked the hand scan. The open-palm step is gone and hand
  // proportions are measured per finger instead, which changes the shape
  // of the vector every finger code and finger sketch is built from - so
  // the same fingertips produce different bits than they did on -5, and
  // the two chains disagree about the material in every claim.
  //
  // -4 -> -5 changed what a claim has to prove. The blink step became a
  // smile, and a hand check was added: a posture sequence and three
  // fingertips, both derived from the same beacon. The same beacon now
  // yields a different challenge, and a mint without hand evidence is
  // refused, so old and new nodes would disagree about every claim.
  //
  // -3 -> -4 was the security review: claims must now register a platform
  // authenticator and be asserted by it, every account carries an ML-DSA
  // key alongside its Ed25519 one, claims past the bootstrap window need a
  // signed vouch, and large transfers are held in escrow. Old nodes reject
  // all of it, so the break is made explicit rather than silent.
  //
  // -2 -> -3 discarded a ledger that had picked up a synthetic identity
  // from local testing. A network whose whole claim is "one real human,
  // one allocation" cannot launch with a fabricated human already in it.
  // The leak is fixed at the source: local development now runs on DEVNET
  // (see the wallet and explorer, which select by origin) instead of
  // gossiping test mints into the live chain.
  chainId: 'face-main-7',
  // 2026-08-25T00:00:00Z - halving epoch 0.
  genesisTimeMs: Date.UTC(2026, 7, 25, 0, 0, 0, 0),
  halvingPeriodMs: 7 * 86_400_000,
  // 65 536 = 2^16, so every halving is exact until the grain floor.
  genesisMintGrains: 65_536n * GRAINS_PER_FACE, // 6_553_600_000_000
  unlockInvites: 5,
  openClaims: 128,
  vouchesPerEpoch: 5,
  guardThresholdGrains: 1_000n * GRAINS_PER_FACE,
  guardWindowMs: 24 * 60 * 60 * 1000,
  deviceChangeWindowMs: 7 * 24 * 60 * 60 * 1000,
  lshPepper: 'face/lsh/v1',
  maxClockSkewMs: 90_000,
  maxBackdateMs: 5_000,
  minParents: 2,
  maxParents: 8,
  basePowBits: 16,
  powRateWindowMs: 60_000,
  powRateFreeTx: 4,
  maxPowBits: 26,
};

export const DEVNET: ChainParams = {
  ...MAINNET,
  guardWindowMs: 2_000,
  deviceChangeWindowMs: 2_000,
  chainId: 'face-dev-7',
  genesisTimeMs: Date.UTC(2026, 7, 25, 0, 0, 0, 0),
  basePowBits: 8,
  maxPowBits: 14,
};

let active: ChainParams = MAINNET;
export function params(): ChainParams {
  return active;
}
export function loadParams(p: ChainParams): void {
  active = p;
}

/* ------------------------------------------------------------------ *
 * Adaptive consensus + throughput parameters.
 *
 * Both security and speed are functions of the live node count. This is
 * the "gets stronger and faster as more people join" property, expressed
 * as actual numbers rather than a slogan:
 *
 *   k  - how many peers each node samples per consensus round. The
 *        probability that an adversary controlling a fraction f of the
 *        network wins a round falls off like f^(alpha), so growing k with
 *        the network shrinks the failure probability exponentially.
 *   beta - consecutive successful rounds required to finalise. Grows
 *        logarithmically, so finality confidence rises with the network.
 *   shards - mempool partitions processed in parallel. Grows with the
 *        node count, so aggregate throughput rises with the network.
 * ------------------------------------------------------------------ */

export interface AdaptiveParams {
  nodes: number;
  k: number;
  alpha: number;
  /**
   * The quorum *ratio* alpha was derived from. Carried explicitly because
   * `alpha / k` is not it: alpha is a ceiling, so for k = 7 the ratio
   * drifts from 0.72 to 0.857, and re-deriving a small-sample quorum from
   * that drifted figure lands back on unanimity - handing any single
   * unresponsive peer a veto over finality.
   */
  alphaRatio: number;
  beta: number;
  shards: number;
  /** Target time between consensus polling rounds, ms. */
  roundMs: number;
}

/** Fraction of a sample that must agree for a vertex to earn a chit. */
export const ALPHA_RATIO = 0.72;

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

export function adaptive(nodeCount: number): AdaptiveParams {
  const n = Math.max(1, Math.floor(nodeCount));
  const k = clamp(Math.ceil(4 * Math.log(n + 1)), 3, 40);
  const alpha = Math.max(2, Math.ceil(ALPHA_RATIO * k));
  const beta = clamp(6 + Math.floor(Math.log2(n + 1)), 6, 30);
  const shards = clamp(1 << Math.max(0, Math.floor(Math.log2(n / 8))), 1, 256);
  // More peers => more parallel queries in flight => shorter rounds, floored
  // so we never melt a phone battery.
  const roundMs = clamp(Math.round(400 - 40 * Math.log2(n + 1)), 60, 400);
  return { nodes: n, k, alpha, alphaRatio: ALPHA_RATIO, beta, shards, roundMs };
}

/** Which parallel shard a sender's traffic belongs to. */
export function shardOf(addressOrKey: string, shards: number): number {
  if (shards <= 1) return 0;
  let h = 2166136261 >>> 0;
  for (let i = 0; i < addressOrKey.length; i++) {
    h ^= addressOrKey.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h % shards;
}
